'use client'
import { signOut, useSession } from 'next-auth/react'
import { useRouter, usePathname } from 'next/navigation'
import { createContext, ReactNode, useEffect, useState } from 'react'
import { isDefined, isNotDefined } from '@halfcall.cn/lib'
import { User } from '@halfcall.cn/prisma'
import { updateUserQuery } from './queries/updateUserQuery'
import { useDebouncedCallback } from 'use-debounce'
import Debugger from '@/utils/debugger'

export const userContext = createContext<{
  user?: User
  isLoading: boolean
  logOut: () => void
  updateUser: (newUser: Partial<User>) => void
}>({
  isLoading: false,
  logOut: () => {
    Debugger.log('logOut not implemented')
  },
  updateUser: () => {
    Debugger.log('updateUser not implemented')
  },
})

const debounceTimeout = 1000
const unsignPages = [
  '/',
  '/login',
  '/register',
]

type UserProviderProps = {
  children: ReactNode
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const router = useRouter()
  const pathName = usePathname()
  const { data: session, status } = useSession()
  const [user, setUser] = useState<User | undefined>()

  useEffect(() => {
    if (isDefined(user) || isNotDefined(session)) return
    const parsedUser = session.user as User
    setUser(parsedUser)
  }, [session, user])

  useEffect(() => {
    if (status === 'loading') return
    const isSigningIn = () => pathName && !unsignPages.includes(pathName)
    if (!user && status === 'unauthenticated' && isSigningIn())
      router.replace(`/register?redirect=${pathName}`)
  }, [status, user])

  const updateUser = (updates: Partial<User>) => {
    if (isNotDefined(user)) return
    const newUser = { ...user, ...updates }
    setUser(newUser)
    saveUser(newUser)?.then()
  }

  const saveUser = useDebouncedCallback(async (newUser?: Partial<User>) => {
    if (isNotDefined(user)) return
    await updateUserQuery(user.id, { ...user, ...newUser })
    await refreshUser()
  }, debounceTimeout)

  const logOut = () => {
    signOut().then()
    setUser(undefined)
  }

  useEffect(() => {
    return () => {
      saveUser.flush()
    }
  }, [saveUser])

  return (
    <userContext.Provider
      value={{
        user,
        isLoading: status === 'loading',
        logOut,
        updateUser,
      }}
    >
      {children}
    </userContext.Provider>
  )
}

export const refreshUser = async () => {
  await fetch('/api/auth/session?update')
  reloadSession()
}

const reloadSession = () => {
  const event = new Event('visibilitychange')
  document.dispatchEvent(event)
}
